import { timesheetsUrl } from 'router/url';
import { RoleTypes } from 'utils/rolesTypes';
import {
  TimesheetApproverFilter,
  TimesheetCategory,
  TimesheetCategoryCounts,
  TimesheetCountsUrlProps,
  TimesheetFilterKey,
  TimesheetFilterValues,
  TimesheetStatusFilter,
  TimesheetUrlProps,
} from './types';

const APPROVER_FILTERS: TimesheetApproverFilter[] = ['mine', 'all'];

// Each status option lists the backend categories it covers. 'all' names
// every category too, so every tab and counter uses the same backend query.
export const TIMESHEET_STATUS_CATEGORIES: Record<
  TimesheetStatusFilter,
  TimesheetCategory[]
> = {
  approved: ['APPROVED', 'SENT_OUT'],
  filled: ['SENT'],
  partial: ['PARTIAL'],
  empty: ['EMPTY'],
  settled: ['SETTLED'],
  all: ['EMPTY', 'PARTIAL', 'SENT', 'APPROVED', 'SENT_OUT', 'SETTLED'],
};

export const TIMESHEET_STATUS_FILTERS: TimesheetStatusFilter[] = [
  'approved',
  'filled',
  'partial',
  'empty',
  'settled',
  'all',
];

// Links shared before the status tabs; 'notApproved' falls back to the default.
const LEGACY_STATUS_FILTERS: Record<string, TimesheetStatusFilter> = {
  waitingForLeader: 'filled',
};

// Leader reminder mails sent `mytimesheets=true` for the leader's own queue.
const LEGACY_MY_TIMESHEETS_FILTERS: Pick<
  TimesheetFilterValues,
  'approver' | 'status'
> = {
  approver: 'mine',
  status: 'filled',
};

export const TIMESHEET_FILTER_KEYS: TimesheetFilterKey[] = [
  'approver',
  'status',
  'showFullTimesheet',
];

export const canSeeAllTimesheets = (roles: string[]) =>
  roles.includes(RoleTypes.RoleAccounting) ||
  roles.includes(RoleTypes.RoleDEVHR);

export const getDefaultTimesheetFilters = (
  canSeeAll: boolean,
): TimesheetFilterValues => ({
  approver: canSeeAll ? 'all' : 'mine',
  status: canSeeAll ? 'all' : 'filled',
  showFullTimesheet: true,
});

// An empty timesheet has nothing to approve, so no leader rule can match it:
// for users who can see all, Empty always covers every timesheet.
export const isApproverLockedToAll = (
  status: TimesheetStatusFilter,
  canSeeAll: boolean,
) => canSeeAll && status === 'empty';

const sum = (values: (number | undefined)[]) =>
  values.reduce<number>((total, value) => total + (value ?? 0), 0);

export const getStatusCount = (
  status: TimesheetStatusFilter,
  counts: TimesheetCategoryCounts,
) =>
  sum(TIMESHEET_STATUS_CATEGORIES[status].map((category) => counts[category]));

const readBoolean = (value: string | null, fallback: boolean) => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return fallback;
};

const readOption = <T extends string>(
  value: string | null,
  options: T[],
  fallback: T,
): T => (options.includes(value as T) ? (value as T) : fallback);

export const readTimesheetFiltersFromQuery = (
  query: URLSearchParams,
  defaults: TimesheetFilterValues,
  canSeeAll: boolean,
): TimesheetFilterValues => {
  const myTimesheets = query.get('mytimesheets') === 'true';
  // The legacy flag only fills the filters the link does not set itself.
  const fallback = <K extends keyof typeof LEGACY_MY_TIMESHEETS_FILTERS>(
    key: K,
  ) =>
    myTimesheets && !query.has(key)
      ? LEGACY_MY_TIMESHEETS_FILTERS[key]
      : defaults[key];
  const statusParam = query.get('status');
  const status = readOption(
    (statusParam && LEGACY_STATUS_FILTERS[statusParam]) || statusParam,
    TIMESHEET_STATUS_FILTERS,
    fallback('status'),
  );
  const approver = canSeeAll
    ? readOption(query.get('approver'), APPROVER_FILTERS, fallback('approver'))
    : 'mine';
  return {
    approver: isApproverLockedToAll(status, canSeeAll) ? 'all' : approver,
    status,
    showFullTimesheet: readBoolean(
      query.get('fullTimesheet'),
      defaults.showFullTimesheet,
    ),
  };
};

export const getChangedTimesheetFilters = (
  values: TimesheetFilterValues,
  defaults: TimesheetFilterValues,
) => TIMESHEET_FILTER_KEYS.filter((key) => values[key] !== defaults[key]);

export const getTimesheetFilterQuery = (
  values: TimesheetFilterValues,
  defaults: TimesheetFilterValues,
) => {
  const params = new URLSearchParams();
  const changed = getChangedTimesheetFilters(values, defaults);
  if (changed.includes('approver')) {
    params.set('approver', values.approver);
  }
  if (changed.includes('status')) {
    params.set('status', values.status);
  }
  if (changed.includes('showFullTimesheet')) {
    params.set('fullTimesheet', String(values.showFullTimesheet));
  }
  const query = params.toString();
  return query ? `&${query}` : '';
};

const getTimesheetCriteriaParams = ({
  month,
  year,
  accountValue,
  clientValue,
  projectValue,
  approver,
  showFullTimesheet,
}: TimesheetCountsUrlProps) =>
  `${typeof year === 'number' ? `&year=${year}` : ''}` +
  `${typeof month === 'number' ? `&month=${month + 1}` : ''}` +
  `${accountValue ? `&accountId=${accountValue}` : ''}` +
  `${projectValue ? `&projectId=${projectValue}` : ''}` +
  `${clientValue ? `&clientId=${clientValue}` : ''}` +
  `${showFullTimesheet ? '&showFullTimesheet=true' : ''}` +
  `${approver === 'mine' ? '&showOnlyCurrentUserToAccept=true' : ''}`;

export const getTimesheetUrlWithParams = ({
  paginationNumber,
  paginationSize,
  status,
  ...criteria
}: TimesheetUrlProps) =>
  `${timesheetsUrl}/summary` +
  `?size=${paginationSize}&page=${paginationNumber}` +
  getTimesheetCriteriaParams(criteria) +
  `&categories=${TIMESHEET_STATUS_CATEGORIES[status].join(',')}`;

export const getTimesheetCountsUrl = (criteria: TimesheetCountsUrlProps) =>
  `${timesheetsUrl}/summary/counts` +
  getTimesheetCriteriaParams(criteria).replace(/^&/, '?');
